import React, { useState, useEffect, useRef } from 'react';
import { useDashboards } from '../../../hooks/useDashboardData';
import { ChevronDown, Plus } from 'lucide-react';
import { JOURNEY_DASHBOARD_DISPLAY_NAME } from '../../../constants/journeyDashboard';

interface DashboardSelectorProps {
  onModeChange?: (mode: 'default' | 'custom' | 'builder') => void;
  currentDashboardName?: string | null;
  isBuilderMode?: boolean;
  viewingSavedDashboardId?: string | null;
  onSelectDashboard?: (dashboardId: string | null) => void;
  onRefreshDashboards?: () => void;
  hideDefault?: boolean;
  /** Rower journey session detail: shared journey template + coach-owned dashboards */
  variant?: 'coach' | 'journey';
  journeyDashboardId?: string;
}

const DashboardSelector: React.FC<DashboardSelectorProps> = ({
  onModeChange,
  currentDashboardName,
  isBuilderMode = false,
  viewingSavedDashboardId,
  onSelectDashboard,
  onRefreshDashboards: _onRefreshDashboards,
  hideDefault = false,
  variant = 'coach',
  journeyDashboardId,
}) => {
  const isJourney = variant === 'journey';
  const [selectedDashboard, setSelectedDashboard] = useState(
    viewingSavedDashboardId ||
      (isBuilderMode ? 'custom' : isJourney && journeyDashboardId ? journeyDashboardId : 'default')
  );

  // Use React Query hook for automatic caching and updates
  const { data: savedDashboards = [], isLoading: isLoadingDashboards } = useDashboards();
  const customDashboards = isJourney
    ? savedDashboards.filter((d) => !d.isDemo && d.id !== journeyDashboardId)
    : savedDashboards;

  // Update selected dashboard when viewingSavedDashboardId changes
  useEffect(() => {
    if (viewingSavedDashboardId) {
      setSelectedDashboard(viewingSavedDashboardId);
    } else if (!isBuilderMode && !isJourney) {
      setSelectedDashboard('default');
    } else if (!isBuilderMode && isJourney && journeyDashboardId) {
      setSelectedDashboard(journeyDashboardId);
    }
  }, [viewingSavedDashboardId, isBuilderMode, isJourney, journeyDashboardId]);

  // Update selected dashboard when mode changes
  useEffect(() => {
    if (isBuilderMode) {
      setSelectedDashboard('custom');
    }
  }, [isBuilderMode]);

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleDashboardChange = (value: string) => {
    setSelectedDashboard(value);
    setIsOpen(false);

    if (value === 'default') {
      onModeChange?.('default');
      onSelectDashboard?.(null);
    } else if (value === 'create-new') {
      onModeChange?.('builder');
      onSelectDashboard?.(null);
    } else if (value === 'custom') {
      onModeChange?.('custom');
    } else {
      // It's a saved dashboard ID
      onSelectDashboard?.(value);
    }
  };

  const getDisplayText = () => {
    if (isJourney && journeyDashboardId && selectedDashboard === journeyDashboardId) {
      return JOURNEY_DASHBOARD_DISPLAY_NAME;
    }
    if (selectedDashboard === 'default') return 'Starter Dashboard';
    if (selectedDashboard === 'custom') return currentDashboardName || 'Custom';
    const selected = savedDashboards.find((d) => d.id === selectedDashboard);
    if (isJourney && selected?.isDemo) return JOURNEY_DASHBOARD_DISPLAY_NAME;
    return selected?.name || (isJourney ? JOURNEY_DASHBOARD_DISPLAY_NAME : 'Starter Dashboard');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="inline-flex">
        <button
          type="button"
          onClick={() => setIsOpen((p) => !p)}
          className="w-[14rem] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-left flex items-center justify-between text-sm"
        >
          <span className={selectedDashboard === 'create-new' ? 'text-green-700' : ''}>
            {isLoadingDashboards ? 'Loading dashboards...' : getDisplayText()}
          </span>
          <ChevronDown className="w-4 h-4 text-gray-400" />
        </button>

        {isOpen && !isLoadingDashboards && (
          <div className="absolute top-full left-0 right-0 mt-1  bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-hidden">
            <div className="max-h-48 overflow-y-auto custom-scrollbar">
              {isJourney && journeyDashboardId && (
                <button
                  type="button"
                  onClick={() => handleDashboardChange(journeyDashboardId)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between border-b border-gray-200"
                >
                  <span>{JOURNEY_DASHBOARD_DISPLAY_NAME}</span>
                  {selectedDashboard === journeyDashboardId && (
                    <span className="text-blue-600">✓</span>
                  )}
                </button>
              )}

              {!hideDefault && !isJourney && (
                <button
                  type="button"
                  onClick={() => handleDashboardChange('default')}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between border-b border-gray-200"
                >
                  <span>Starter Dashboard</span>
                  {selectedDashboard === 'default' && <span className="text-blue-600">✓</span>}
                </button>
              )}

              {isBuilderMode && currentDashboardName && (
                <button
                  type="button"
                  onClick={() => handleDashboardChange('custom')}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between"
                >
                  <span>{currentDashboardName}</span>
                  {selectedDashboard === 'custom' && <span className="text-blue-600">✓</span>}
                </button>
              )}

              {customDashboards.map((dashboard) => (
                <button
                  key={dashboard.id}
                  type="button"
                  onClick={() => handleDashboardChange(dashboard.id)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between"
                >
                  <span>{dashboard.name}</span>
                  {selectedDashboard === dashboard.id && <span className="text-blue-600">✓</span>}
                </button>
              ))}
            </div>

            {/* Create New Button - Fixed at bottom */}
            <div className="border-t border-gray-200 bg-gray-50 mt-1">
              <button
                type="button"
                onClick={() => handleDashboardChange('create-new')}
                className="w-full px-3 py-2 text-left hover:bg-green-50 text-green-700 font-medium flex items-center gap-2"
              >
                <Plus size={16} className="text-green-600" />
                Create New
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardSelector;
